import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Sparkles, Trash2, Globe, Minimize2 } from "lucide-react";
import axios from "axios";

import { API } from "@/config";

const SUGGESTIONS = [
  "Who is PM of Nepal?",
  "Latest Nepal politics",
  "About Balen Shah",
  "Explain democracy",
  "G20 leaders 2026",
];

const AIChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [webSearch, setWebSearch] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (isOpen && !isMinimized) {
      inputRef.current?.focus();
    }
  }, [isOpen, isMinimized]);

  const sendMessage = async (text) => {
    if (!text.trim() || loading) return;

    const userMessage = { role: "user", content: text };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      const response = await axios.post(`${API}/ai/chat`, {
        message: text,
        session_id: sessionId,
        web_search: webSearch,
      });
      
      setSessionId(response.data.session_id);
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: response.data.response },
      ]);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Sorry, I couldn't process that. Please try again." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const clearChat = async () => {
    if (sessionId) {
      try {
        await axios.delete(`${API}/ai/chat/${sessionId}`);
      } catch (e) {}
    }
    setMessages([]);
    setSessionId(null);
  };

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  return (
    <>
      {/* Floating Button */}
      <motion.button
        className={`fixed z-50 shadow-lg flex items-center justify-center text-white transition-all
          ${isOpen ? 'bottom-4 right-4 w-12 h-12 sm:bottom-6 sm:right-6' : 'bottom-4 right-4 w-14 h-14 sm:bottom-6 sm:right-6'}
          bg-gradient-to-r from-gorkhali-red to-peace-blue rounded-full hover:scale-110`}
        onClick={() => {
          if (isOpen) {
            setIsOpen(false);
            setIsMinimized(false);
          } else {
            setIsOpen(true);
          }
        }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        data-testid="ai-chat-toggle"
        style={{ display: isOpen && !isMinimized ? 'none' : 'flex' }}
      >
        <Sparkles size={24} />
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ 
              opacity: 1, 
              y: 0, 
              scale: 1,
              height: isMinimized ? 'auto' : undefined
            }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            className={`fixed z-50 bg-night-count border border-white/20 shadow-2xl flex flex-col overflow-hidden
              ${isMinimized 
                ? 'bottom-4 right-4 w-64 rounded-2xl' 
                : 'bottom-0 right-0 w-full h-full sm:bottom-6 sm:right-6 sm:w-96 sm:h-[520px] sm:rounded-2xl rounded-none'
              }`}
            data-testid="ai-chat-window"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-gorkhali-red to-peace-blue p-3 sm:p-4 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2">
                <Sparkles className="text-white" size={20} />
                <div>
                  <h3 className="font-bebas text-white tracking-wider text-sm sm:text-base">POLITICS AI</h3>
                  {!isMinimized && (
                    <p className="text-xs text-white/70 hidden sm:block">Ask about Nepal & World Politics</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1">
                {!isMinimized && (
                  <>
                    <button
                      onClick={() => setWebSearch(!webSearch)}
                      className={`p-1.5 rounded-full transition-colors ${webSearch ? 'bg-white/20 text-white' : 'text-white/50'}`}
                      title={webSearch ? "Web search ON" : "Web search OFF"}
                    >
                      <Globe size={16} />
                    </button>
                    <button
                      onClick={clearChat}
                      className="text-white/70 hover:text-white p-1.5"
                      title="Clear chat"
                    >
                      <Trash2 size={16} />
                    </button>
                  </>
                )}
                <button
                  onClick={toggleMinimize}
                  className="text-white/70 hover:text-white p-1.5 hidden sm:block"
                  title={isMinimized ? "Expand" : "Minimize"}
                >
                  <Minimize2 size={16} />
                </button>
                <button
                  onClick={() => { setIsOpen(false); setIsMinimized(false); }}
                  className="text-white/70 hover:text-white p-1.5"
                  title="Close"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {!isMinimized && (
              <>
                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3">
                  {messages.length === 0 && (
                    <div className="text-center py-6 sm:py-8">
                      <Sparkles className="mx-auto text-marigold mb-3" size={28} />
                      <p className="text-gray-400 text-sm mb-4">Ask me about politics!</p>
                      <div className="flex flex-wrap gap-2 justify-center px-2">
                        {SUGGESTIONS.map((s, i) => (
                          <button
                            key={i}
                            onClick={() => sendMessage(s)}
                            className="text-xs bg-white/10 hover:bg-white/20 text-gray-300 px-3 py-1.5 rounded-full transition-colors"
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                      <div className="mt-4 flex items-center justify-center gap-2 text-xs text-gray-500">
                        <Globe size={12} />
                        <span>Web search {webSearch ? 'enabled' : 'disabled'}</span>
                      </div>
                    </div>
                  )}

                  {messages.map((msg, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[90%] sm:max-w-[85%] px-3 sm:px-4 py-2 rounded-2xl text-sm leading-relaxed ${
                          msg.role === "user"
                            ? "bg-gorkhali-red text-white rounded-br-sm"
                            : "bg-white/10 text-gray-200 rounded-bl-sm"
                        }`}
                      >
                        {msg.content}
                      </div>
                    </motion.div>
                  ))}

                  {loading && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="flex justify-start"
                    >
                      <div className="bg-white/10 px-4 py-3 rounded-2xl rounded-bl-sm">
                        <div className="flex gap-1">
                          <span className="w-2 h-2 bg-marigold rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                          <span className="w-2 h-2 bg-marigold rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                          <span className="w-2 h-2 bg-marigold rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                        </div>
                      </div>
                    </motion.div>
                  )}

                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <div className="p-3 border-t border-white/10 shrink-0 bg-night-count">
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      sendMessage(input);
                    }}
                    className="flex gap-2"
                  >
                    <input
                      ref={inputRef}
                      type="text"
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      placeholder="Ask about politics..."
                      className="flex-1 bg-white/10 border border-white/20 rounded-full px-4 py-2.5 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-marigold"
                      disabled={loading}
                      data-testid="ai-chat-input"
                    />
                    <button
                      type="submit"
                      disabled={loading || !input.trim()}
                      className="w-10 h-10 bg-marigold hover:bg-marigold/80 disabled:bg-gray-600 rounded-full flex items-center justify-center text-white transition-colors shrink-0"
                      data-testid="ai-chat-send"
                    >
                      <Send size={18} />
                    </button>
                  </form>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AIChatWidget;
